/**
 * @copyright (C) 2023-2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-03 14:00:45
 **/

import styled from "@emotion/styled";

import { Button, Tab, TabItem } from "@/frameworks/designStuff/components";
import { Column, Row } from "@/frameworks/designStuff/components";
import { Drawer } from "@/frameworks/designStuff/compounds";
import { CallType } from "./commons/UserVideoWithAvatar";

interface VideoType {
	callType?: CallType;
	isSpeaking?: boolean;
	micStatus?: MediaState;
	isHover?: boolean;
	fullScreen?: boolean;
}

export const Wrap = styled(Drawer.Wrap)`
	width: 100%;
`;

export const Body = styled(Drawer.Body)`
	padding: 16px 20px;
	width: 100%;
`;

export const HeaderIconList = styled(Row.div)`
	gap: 12px;
	width: 100%;
	padding: 10px 12px;
`;

export const CallButton = styled.button<{ status: string }>`
	width: 136px;
	height: 40px;
	display: flex;
	border-radius: 20px;
	border: none;
	background-color: ${({ status, theme }) =>
		status === "idle" ? theme.colors.success_60_100 : theme.colors.error_60_100};
	cursor: pointer;
	justify-content: center;
	align-items: center;
`;

export const InfoWrap = styled(Column)`
	padding: 17px 21px 0;
	gap: 20px;
`;

export const StyledTab = styled(Tab)`
	width: 100%;
`;

export const StyledTabItem = styled(TabItem)`
	flex: 1;
	text-align: center;
`;

export const EmojiImage = styled.img`
	width: 24px;
	height: 24px;
`;

export const TabWrapper = styled(Row)`
	width: 100%;
	height: 50%;
`;

export const TabArea = styled.div`
	width: 90%;
	height: 80%;
`;

export const Video = styled.video<{ fullScreen?: boolean }>`
	width: 100%;
	height: 100%;
	object-fit: contain;
	background-color: ${({ fullScreen, theme }) => (fullScreen ? theme.colors.neutral_80_90 : "black")};
	color: white;
	border-radius: ${({ fullScreen }) => (fullScreen ? "0px" : "6px")};
	backdrop-filter: ${({ fullScreen }) => (fullScreen ? "blur(20px)" : undefined)};
`;

export const AvatarContainer = styled.div<VideoType>`
	position: relative;
	background-color: ${({ callType, theme }) =>
		callType === "calling"
			? theme.colors.success_80_100
			: callType === "no_response"
				? theme.colors.error_80_100
				: callType === "before_call"
					? theme.colors.error_60_100
					: "black"};
	${({ isSpeaking, theme, isHover }) =>
		isSpeaking && isHover
			? `border: 2px solid ${theme.colors.accent_40_100};`
			: isSpeaking
				? `	border: 3px solid transparent;
			border: 6px solid transparent;
			background-image: linear-gradient(#000,#000),
			${theme.colors.gradient_40_100};
			background-origin: border-box;
			background-clip: padding-box, border-box;`
				: ""}
	border-radius: 6px;
`;

export const AvatarWrapper = styled.div<VideoType>`
	box-sizing: border-box;
	position: absolute;
	top: 50%;
	left: 50%;
	background-color: ${({ callType, fullScreen, theme }) =>
		callType === "calling"
			? theme.colors.success_60_100
			: callType === "no_response"
				? theme.colors.error_60_100
				: callType === "none" && fullScreen
					? theme.colors.neutral_80_90
					: "black"};
	${({ callType, theme, isHover }) =>
		callType === "calling" && isHover ? `border: 2px solid ${theme.colors.accent_40_100};` : ""}
	border-radius: ${({ fullScreen }) => (fullScreen ? "0px" : "6px")};
	backdrop-filter: ${({ fullScreen }) => (fullScreen ? "blur(20px)" : undefined)};
	transform: translate(-50%, -50%);
	display: flex;
	justify-content: center;
	align-items: center;
	width: ${({ callType, isSpeaking }) => (callType !== "none" || isSpeaking ? "95%" : "100%")};
	height: ${({ callType, isSpeaking }) => (callType !== "none" || isSpeaking ? "90%" : "100%")};
`;

export const RightBottomIconWrapper = styled.div`
	position: absolute;
	bottom: -15px;
	right: -15px;
	transform: translate(-50%, -50%);
`;

export const LeftTopNameWrapper = styled.div<VideoType>`
	position: absolute;
	top: ${({ callType, isSpeaking }) => (callType !== "none" || isSpeaking ? "10px" : "5px")};
	left: 0;
	background-color: ${({ theme }) => theme.colors.neutral_80_16};
	color: white;
	border-radius: 4px;
	padding: 2px 6px;
	display: inline-block;
	max-width: 90%;
	white-space: nowrap;
	margin-left: 10px;
`;

export const IconWrapLocation = styled.span`
	height: 16px;
	width: 16px;
	display: block;
`;

export const ButtonLocation = styled(Button)`
	width: 100%;
	margin-bottom: 20px;
	.buttonText {
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 4px;
	}
`;

export const LeftBottomIconWrapper = styled.div<VideoType>`
	position: absolute;
	bottom: ${({ callType }) => (callType !== "none" ? "-4px" : "-10px")};
	left: ${({ callType }) => (callType !== "none" ? "28px" : "20px")};
	transform: translate(-50%, -50%);
`;

export const RightTopIconWrapper = styled.div``;

export const IconWrapper = styled.div<VideoType>`
	width: 30px;
	height: 30px;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: ${({ theme, callType, micStatus }) =>
		callType === "no_response"
			? theme.colors.error_80_100
			: callType === "calling"
				? theme.colors.success_80_100
				: micStatus !== "on"
					? theme.colors.error_60_100
					: "none"};
`;
